import React from "react";
import "./home.css";
import { DataLayerContext } from "../dataLayer/DataLayer";
import { IPuzzleV1 } from "../dataLayer/v1/interfaces/classes/IPuzzleV1";
import { SimpleDate } from "../dataLayer/SimpleDate";
import { getSimpleDateInSeattle } from "../dataLayer/getSimpleDateInSeattle";
import {
  Stats as StatsIcon,
  Question,
  Mail,
  ChevronRight,
  Trophy,
  Settings,
} from "./components/icons";
import { HomeBrand } from "./components/homeBrand";
import { HelpPanel } from "./components/helpPanel";
import { googleAuth } from "../auth/googleAuth";
import { appleAuth } from "../auth/appleAuth";
import { authState, hasOperationsAccess, signOut as signOutAll } from "../auth/authState";
import { useObservableProperty } from "../observable/useObservableProperty";
import { isTestMode } from "../testMode";
import { TierStar } from "./components/tierStar";
import { hasPuzzleTimeExpired } from "../gameLogic/puzzleTimer";

const HELP_FIRST_RUN_KEY = "WirderHasShownHelp";
const HOME_SCROLL_TOP_KEY = "WirderHomeScrollTop";

function toDate(d: SimpleDate): Date {
  return new Date(d.year, d.month - 1, d.day);
}
function formatLongDate(d: SimpleDate): string {
  return toDate(d).toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
  });
}
function formatShortDay(d: SimpleDate): string {
  return toDate(d).toLocaleDateString(undefined, { weekday: "short" });
}
function formatShortDate(d: SimpleDate): string {
  return toDate(d).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}
/** Whole days from `a` to `b` (positive when `b` is later). DST-safe via UTC. */
function daysBetween(a: SimpleDate, b: SimpleDate): number {
  const ms = Date.UTC(b.year, b.month - 1, b.day) - Date.UTC(a.year, a.month - 1, a.day);
  return Math.round(ms / 86_400_000);
}
function formatClock(totalSeconds: number): string {
  const m = Math.floor(totalSeconds / 60);
  const s = Math.floor(totalSeconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function TodayHero({ puzzle }: { puzzle: IPuzzleV1 }) {
  const dataLayer = React.useContext(DataLayerContext);
  const totalScore = useObservableProperty<number>(puzzle.totalScore);
  const regularPoints = useObservableProperty<number | undefined>(puzzle.regularPoints);
  const foundWords = useObservableProperty(puzzle.foundWords);
  const elapsed = puzzle.timeElapsedInSeconds || 0;
  const remaining = Math.max(0, puzzle.timeLimitInSeconds - elapsed);
  const started = !puzzle.played && (foundWords.length > 0 || elapsed > 0);

  const onPlay = () => {
    dataLayer.currentPuzzle.value = puzzle;
    dataLayer.navigateTo(hasPuzzleTimeExpired(puzzle) ? "SubmittingSolution" : "Puzzle");
  };
  const onReview = () => {
    dataLayer.currentPuzzle.value = puzzle;
    dataLayer.navigateTo("ReviewPuzzle");
  };

  return (
    <div className={"home__hero card card--hero" + (puzzle.played ? "" : " home__hero--reserved")}>
      <span className="eyebrow eyebrow--accent">Today&rsquo;s puzzle</span>
      <div className="display hero__date">{formatLongDate(puzzle.date)}</div>

      {puzzle.played ? (
        <>
          <div className="hero__played">
            <div className="hero__stat">
              <div className="hero__stat-val num">{foundWords.length}</div>
              <div className="hero__stat-lbl">
                {foundWords.length === 1 ? "word" : "words"} found
              </div>
            </div>
            <div className="hero__stat">
              <div className="hero__stat-val num">
                {(regularPoints ?? totalScore).toLocaleString()}
              </div>
              <div className="hero__stat-lbl">regular points</div>
            </div>
          </div>
          <div className="hero__pending">
            <Clock16 />
            <span>
              Come back <b>tomorrow</b> for rare-word bonuses and your total score.
            </span>
          </div>
          <button className="btn btn--primary" onClick={onReview}>
            See your results
          </button>
        </>
      ) : (
        <>
          <div className="hero__clock">
            <Clock16 />
            {started ? (
              <span>
                <b className="num">{formatClock(remaining)}</b> left on the clock
              </span>
            ) : (
              <span>
                <b className="num">{formatClock(puzzle.timeLimitInSeconds)}</b> on the clock once
                you begin
              </span>
            )}
          </div>
          {started && foundWords.length > 0 && (
            <div className="hero__sofar">
              Score so far <b className="num">{totalScore.toLocaleString()}</b> &middot; finish
              before the day ends!
            </div>
          )}
          <button className="btn btn--primary" onClick={onPlay}>
            {started ? "Continue today\u2019s hunt" : "Play today\u2019s hunt"}
          </button>
        </>
      )}
    </div>
  );
}

function Clock16() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className="hero__clock-icon" aria-hidden="true">
      <path d="M3.5 12C3.5 7.30558 7.30558 3.5 12 3.5C16.6944 3.5 20.5 7.30558 20.5 12C20.5 16.6944 16.6944 20.5 12 20.5C7.30558 20.5 3.5 16.6944 3.5 12ZM12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2ZM11.9931 6.64827C11.9435 6.28233 11.6295 6 11.25 6C10.836 6 10.5 6.336 10.5 6.75V12.75L10.5069 12.8517C10.5565 13.2177 10.8705 13.5 11.25 13.5H15.25L15.3517 13.4931C15.7177 13.4435 16 13.1295 16 12.75C16 12.336 15.664 12 15.25 12H12V6.75L11.9931 6.64827Z" />
    </svg>
  );
}

/**
 * Hero card shown while today's puzzle hasn't been published yet and we're
 * poking the server + re-checking the CDN. Mirrors TodayHero's framing so the
 * layout doesn't jump when the real puzzle arrives.
 */
function TodayLoadingCard() {
  const today = getSimpleDateInSeattle();
  return (
    <div className="home__hero home__hero--reserved card card--hero" aria-busy="true">
      <span className="eyebrow eyebrow--accent">Today&rsquo;s puzzle</span>
      <div className="display hero__date">{formatLongDate(today)}</div>
      <div className="hero__loading">
        <div className="hero__dots" aria-label="Loading today&rsquo;s puzzle">
          <i></i>
          <i></i>
          <i></i>
        </div>
        <span>Fetching today&rsquo;s hunt&hellip;</span>
      </div>
    </div>
  );
}

/**
 * Hero card shown when the poke + retry budget elapsed without today's puzzle
 * appearing. Offers a "check again" action that re-pokes the server.
 */
function TodayNotReadyCard() {
  const dataLayer = React.useContext(DataLayerContext);
  const today = getSimpleDateInSeattle();
  return (
    <div className="home__hero home__hero--reserved card card--hero">
      <span className="eyebrow eyebrow--accent">Today&rsquo;s puzzle</span>
      <div className="display hero__date">{formatLongDate(today)}</div>
      <div className="hero__pending">
        <Clock16 />
        <span>
          Today&rsquo;s hunt isn&rsquo;t ready just yet. We&rsquo;ll keep checking and it&rsquo;ll
          appear here automatically &mdash; or tap below to check now.
        </span>
      </div>
      <button className="btn btn--primary" onClick={() => dataLayer.retryTodaysPuzzle()}>
        Check again
      </button>
    </div>
  );
}

function DayCard({ puzzle }: { puzzle: IPuzzleV1 }) {
  const dataLayer = React.useContext(DataLayerContext);
  const totalScore = useObservableProperty<number>(puzzle.totalScore);
  const foundWords = useObservableProperty(puzzle.foundWords);
  const onClick = () => {
    dataLayer.currentPuzzle.value = puzzle;
    dataLayer.navigateTo("ReviewPuzzle");
  };
  return (
    <button className="daycard clickable" onClick={onClick}>
      <div className="daycard__head">
        <div className="daycard__day">{formatShortDay(puzzle.date)}</div>
        <div className="daycard__date">{formatShortDate(puzzle.date)}</div>
      </div>
      <div className="daycard__body">
        <div className="daycard__score num">
          {totalScore.toLocaleString()}
          <small>points</small>
        </div>
        <div className="daycard__words num">
          {foundWords.length}
          <small>{foundWords.length === 1 ? "word" : "words"}</small>
        </div>
      </div>
    </button>
  );
}

export function HomeScreen() {
  const dataLayer = React.useContext(DataLayerContext);
  const auth = useObservableProperty(authState);
  const todayStatus = useObservableProperty(dataLayer.todayPuzzleStatus);
  const contentRef = React.useRef<HTMLDivElement>(null);
  const overlay = useObservableProperty(dataLayer.currentOverlay);
  const premiumNotice = useObservableProperty(dataLayer.premiumNotice);
  const premium = useObservableProperty(dataLayer.premium);
  const showHelp = overlay === "help";
  const verifiedPremiumTier = premium.status === "ready" ? premium.tier : null;

  const puzzles = dataLayer.puzzles;
  const today =
    puzzles.length > 0 && puzzles[puzzles.length - 1].isToday
      ? puzzles[puzzles.length - 1]
      : undefined;
  const past = (today ? puzzles.slice(0, puzzles.length - 1) : puzzles).slice().reverse();
  // Only surface days the player actually completed; opened-but-skipped days are
  // dropped. `past` is most-recent-first, so a gap of more than one day between
  // adjacent played cards means the run is non-contiguous and gets an ellipsis
  // marker rendered between them.
  const playedPast = past.filter((p) => p.played);

  const onOpenPlayerStats = () => {
    dataLayer.navigateTo("PlayerStats");
  };
  const onOpenLeaderboard = () => {
    dataLayer.openLeaderboard();
  };
  const onSignOut = () => {
    googleAuth.clearCachedToken();
    appleAuth.clearCachedToken();
    signOutAll();
    dataLayer.resetSession();
  };

  const signedInDisplay =
    auth.status === "signedIn" ? auth.identity.email ?? auth.identity.name : undefined;

  // On first ever load, open the "How to play" overlay as its own history entry
  // (so Back / swipe-back closes it), and mark it seen immediately so however
  // it's dismissed it won't reappear. Guarded so it runs at most once per mount.
  // Skipped in test mode so scenarios land deterministically on Home rather than
  // popping the first-run help overlay depending on localStorage state.
  const firstRunHandledRef = React.useRef(false);
  React.useEffect(() => {
    if (firstRunHandledRef.current) {
      return;
    }
    firstRunHandledRef.current = true;
    if (isTestMode()) {
      return;
    }
    if (localStorage.getItem(HELP_FIRST_RUN_KEY) !== "1") {
      localStorage.setItem(HELP_FIRST_RUN_KEY, "1");
      dataLayer.openOverlay("help");
    }
  }, [dataLayer]);

  React.useEffect(() => {
    if (premium.status === "idle") {
      void dataLayer.loadPremiumStatus();
    }
  }, [dataLayer, premium.status]);

  // Home unmounts when you open a full screen like Stats, so its scroll position
  // is restored from sessionStorage on remount. The subtlety: WebKit lays out the
  // freshly-mounted Home with fallback font metrics for one frame, then applies
  // the (already-loaded) web font and grows the content ~19px. A one-shot restore
  // clamps against the momentarily-shorter content and lands a little high — the
  // "scrolls up a bit" bug. So we restore synchronously pre-paint and then
  // re-assert via a ResizeObserver whenever the content resizes, until we reach
  // the saved position. This is event-driven (no timers, no race): it reacts to
  // the actual reflow. We hand off the moment the user starts scrolling so we
  // never fight them.
  React.useLayoutEffect(() => {
    if (typeof window === "undefined" || typeof ResizeObserver === "undefined") {
      return;
    }
    const content = contentRef.current;
    if (!content) {
      return;
    }
    const savedTop = Number(sessionStorage.getItem(HOME_SCROLL_TOP_KEY) ?? "0");
    if (!Number.isFinite(savedTop) || savedTop <= 0) {
      return;
    }

    let finished = false;
    const cleanup = () => {
      if (finished) {
        return;
      }
      finished = true;
      observer.disconnect();
      content.removeEventListener("wheel", handoff);
      content.removeEventListener("touchmove", handoff);
      content.removeEventListener("pointerdown", handoff);
      content.removeEventListener("keydown", handoff);
    };

    // Re-assert the saved position against the current (possibly still-growing)
    // content height. Once the content is finally tall enough to honor the full
    // saved offset, the restore is complete.
    const reassert = () => {
      const maxTop = content.scrollHeight - content.clientHeight;
      const target = Math.max(0, Math.min(savedTop, maxTop));
      if (Math.abs(content.scrollTop - target) > 1) {
        content.scrollTop = target;
      }
      if (maxTop >= savedTop - 1) {
        cleanup();
      }
    };

    // If the user takes over scrolling, stop restoring immediately.
    const handoff = () => cleanup();

    const observer = new ResizeObserver(() => {
      if (!finished) {
        reassert();
      }
    });

    // Apply once synchronously (pre-paint) so there's never a flash at the top,
    // then watch for the reflow that grows the content.
    reassert();
    if (!finished) {
      observer.observe(content);
      for (const child of Array.from(content.children)) {
        observer.observe(child);
      }
      content.addEventListener("wheel", handoff, { passive: true });
      content.addEventListener("touchmove", handoff, { passive: true });
      content.addEventListener("pointerdown", handoff, { passive: true });
      content.addEventListener("keydown", handoff);
    }

    return cleanup;
  }, []);

  React.useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    const persistScrollTop = () => {
      if (contentRef.current) {
        sessionStorage.setItem(HOME_SCROLL_TOP_KEY, String(contentRef.current.scrollTop));
      }
    };
    const content = contentRef.current;
    content?.addEventListener("scroll", persistScrollTop, { passive: true });
    return () => {
      persistScrollTop();
      content?.removeEventListener("scroll", persistScrollTop);
    };
  }, []);

  return (
    <div className="screen home">
      <div className="home__content" ref={contentRef}>
        <HomeBrand />

        {premiumNotice && (
          <div className="home__premium-notice" role="status">
            <span className="home__premium-notice-message">{premiumNotice.message}</span>
            <div className="home__premium-notice-actions">
              {premiumNotice.action === "join" && (
                <button onClick={() => dataLayer.navigateTo("Settings")}>View premium</button>
              )}
              <button onClick={() => dataLayer.clearPremiumNotice()}>Dismiss</button>
            </div>
          </div>
        )}

        {today ? (
          <TodayHero puzzle={today} />
        ) : todayStatus === "loading" ? (
          <TodayLoadingCard />
        ) : todayStatus === "notReady" ? (
          <TodayNotReadyCard />
        ) : null}

        {playedPast.length > 0 && (
          <div className="recent">
            <div className="recent__head">
              <span className="eyebrow">Recent days</span>
            </div>
            <div className="recent__row">
              {playedPast.map((p, i) => {
                const prev = playedPast[i - 1];
                const gap = prev ? daysBetween(p.date, prev.date) > 1 : false;
                const key = `${p.date.year}-${p.date.month}-${p.date.day}`;
                return (
                  <React.Fragment key={key}>
                    {gap && (
                      <div className="recent__gap" aria-label="Days not played" role="separator">
                        &hellip;
                      </div>
                    )}
                    <DayCard puzzle={p} />
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        )}

        <div className="home__foot">
          <div className="linklist">
            <button className="listrow" onClick={onOpenLeaderboard}>
              <Trophy className="lead" />
              <span className="home__premium-link">
                Leaderboard
                <span className="home__premium-icon">
                  <TierStar tier={verifiedPremiumTier} variant="home" />
                </span>
              </span>
              <ChevronRight className="chev" />
            </button>
            <button className="listrow" onClick={onOpenPlayerStats}>
              <StatsIcon className="lead" />
              Your stats
              <ChevronRight className="chev" />
            </button>
            <button className="listrow" onClick={() => dataLayer.openOverlay("help")}>
              <Question className="lead" />
              How to play
              <ChevronRight className="chev" />
            </button>
            <button className="listrow" onClick={() => dataLayer.navigateTo("Settings")}>
              <Settings className="lead" />
              Settings
              <ChevronRight className="chev" />
            </button>
            {hasOperationsAccess() && (
              <button className="listrow" onClick={() => dataLayer.openOperationsDashboard()}>
                <StatsIcon className="lead" />
                Operations
                <ChevronRight className="chev" />
              </button>
            )}
            <button className="listrow" onClick={() => dataLayer.openFeedback()}>
              <Mail className="lead" />
              Send feedback
              <ChevronRight className="chev" />
            </button>
          </div>
          <div className="signed">
            {signedInDisplay && <span>Signed in as {signedInDisplay} &middot; </span>}
            <a
              href="#"
              className="signed__out"
              onClick={(e) => {
                e.preventDefault();
                onSignOut();
              }}
            >
              Sign out
            </a>
          </div>
        </div>
      </div>

      <HelpPanel open={showHelp} onClose={() => dataLayer.closeOverlay()} />
    </div>
  );
}
